import { useEffect, useMemo, useState } from 'react';
import api, { AuditEntry } from '../services/api';

/**
 * Audit log page.
 *
 * Lists the 200 most-recent audit rows and offers a "CSV exportieren"
 * button that hits the streaming `GET /api/audit.csv` endpoint with
 * the active filters applied. The button is disabled until the
 * operator picks at least one filter (refuses an unrestricted dump
 * — the backend repeats the check, but disabling at the UI level
 * gives a faster failure for human users).
 *
 * Welle 4.6 QoL-2.
 */
export function AuditLogPage() {
  const [rows, setRows] = useState<AuditEntry[]>([]);
  const [loading, setLoading] = useState(true);

  // Filter state. All optional; the export button activates as soon
  // as ANY one is set (matches the backend `parseCsvFilter` rule).
  const [tenantId, setTenantId] = useState('');
  const [action, setAction] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');

  // Export modal state.
  const [exportOpen, setExportOpen] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);
  const [exportSuccess, setExportSuccess] = useState<string | null>(null);

  useEffect(() => {
    api.get<{ rows: AuditEntry[] }>('/audit', { params: { limit: 200 } })
      .then((r) => setRows(r.data.rows))
      .finally(() => setLoading(false));
  }, []);

  const hasFilter = useMemo(
    () => Boolean(tenantId || action || from || to),
    [tenantId, action, from, to],
  );

  // Build the query string from the active filters. Mirrors the
  // backend's expected param names exactly (tenantId, action, from,
  // to). Empty fields are dropped so the backend's required-filter
  // check uses the same view as the user.
  function buildExportQuery(): string {
    const p = new URLSearchParams();
    if (tenantId) p.set('tenantId', tenantId);
    if (action) p.set('action', action);
    if (from) {
      // Allow either an ISO date (yyyy-mm-dd from <input type="date">)
      // or a full ISO timestamp. The backend's Date.parse accepts
      // both — we just normalise the date-only case to a midnight
      // UTC so the auditor's window endpoint is unambiguous.
      const v = /T\d/.test(from) ? from : `${from}T00:00:00Z`;
      p.set('from', v);
    }
    if (to) {
      // Same normalisation for the upper bound — but to the END of
      // the day so a window like "from 2026-05-01 to 2026-05-28"
      // includes all of May 28th.
      const v = /T\d/.test(to) ? to : `${to}T23:59:59Z`;
      p.set('to', v);
    }
    return p.toString();
  }

  /**
   * Trigger the download.
   *
   * We use the fetch+Blob path rather than a plain <a download>
   * because the Authorization header must be attached manually
   * (axios's interceptor handles this for JSON calls, but a raw
   * anchor tag would not). The download then offers via a hidden
   * anchor + URL.createObjectURL — same pattern as a vanilla "Save
   * As" prompt.
   */
  async function runExport(): Promise<void> {
    setExportOpen(true);
    setExportError(null);
    setExportSuccess(null);
    try {
      const qs = buildExportQuery();
      // axios returns the body as a Blob when responseType: 'blob';
      // the auth interceptor still injects the Bearer token.
      const res = await api.get(`/audit.csv?${qs}`, { responseType: 'blob' });
      const blob = res.data as Blob;
      // Filename comes from the backend's Content-Disposition;
      // fall back to a sensible default if the header is missing
      // or unparseable (e.g. in tests / via service workers).
      const cd = String(res.headers['content-disposition'] ?? '');
      const m = cd.match(/filename="?([^"]+)"?/);
      const filename = m ? m[1] : 'audit-export.csv';

      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      // Free the Blob URL after a short delay so the browser has a
      // chance to start the download. revokeObjectURL synchronously
      // tears down the URL — calling it too early can abort the
      // save dialog on some Chromium versions.
      setTimeout(() => URL.revokeObjectURL(url), 1_000);

      setExportSuccess(`Download gestartet: ${filename}`);
    } catch (err) {
      // The backend may have returned a JSON error wrapped as Blob;
      // surface whatever text we can extract so the operator knows
      // why the export failed (the most common case is 400 "no
      // filter provided", which the UI guard usually prevents).
      const e = err as { response?: { data?: Blob; status?: number } };
      let msg = 'Export fehlgeschlagen';
      if (e.response?.data instanceof Blob) {
        try {
          const text = await e.response.data.text();
          msg = `${msg} (${e.response.status}): ${text}`;
        } catch { /* keep default message */ }
      }
      setExportError(msg);
    }
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-semibold">Audit-Log</h2>
        <button
          type="button"
          onClick={runExport}
          disabled={!hasFilter}
          title={hasFilter
            ? 'Exportiert das gefilterte Audit-Log als CSV'
            : 'Bitte mindestens einen Filter setzen (Tenant, Aktion, Zeitraum)'}
          className={
            'px-3 py-1 rounded text-sm font-medium ' +
            (hasFilter
              ? 'bg-neutral-900 text-white hover:bg-neutral-700'
              : 'bg-slate-200 text-slate-500 cursor-not-allowed')
          }
        >
          CSV exportieren
        </button>
      </div>

      {/* Filter strip. Filters apply to the export query string AND
          to the in-page table preview — operators expect the export
          to mirror what they see. */}
      <div className="flex flex-wrap items-end gap-3 mb-4 bg-white shadow rounded p-3">
        <label className="text-sm">
          <span className="block text-slate-500 mb-1">Tenant-ID</span>
          <input
            type="text"
            value={tenantId}
            onChange={(e) => setTenantId(e.target.value.trim())}
            placeholder="tnt-uuid"
            className="px-2 py-1 rounded border w-48 font-mono text-xs"
          />
        </label>
        <label className="text-sm">
          <span className="block text-slate-500 mb-1">Aktion (Präfix)</span>
          <input
            type="text"
            value={action}
            onChange={(e) => setAction(e.target.value.trim())}
            placeholder="tenant."
            className="px-2 py-1 rounded border w-48 font-mono text-xs"
          />
        </label>
        <label className="text-sm">
          <span className="block text-slate-500 mb-1">Von</span>
          <input
            type="date"
            value={from}
            onChange={(e) => setFrom(e.target.value)}
            className="px-2 py-1 rounded border"
          />
        </label>
        <label className="text-sm">
          <span className="block text-slate-500 mb-1">Bis</span>
          <input
            type="date"
            value={to}
            onChange={(e) => setTo(e.target.value)}
            className="px-2 py-1 rounded border"
          />
        </label>
      </div>

      {loading ? <p className="text-slate-500">Lade…</p> : (
        <table className="w-full bg-white shadow rounded text-sm">
          <thead className="bg-slate-100 text-left">
            <tr>
              <th className="p-2">Zeit</th>
              <th className="p-2">Aktion</th>
              <th className="p-2">Operator</th>
              <th className="p-2">Tenant</th>
              <th className="p-2">Payload</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((e) => (
              <tr key={e.id} className="border-t">
                <td className="p-2 text-slate-500">{new Date(e.createdAt).toLocaleString()}</td>
                <td className="p-2 font-mono">{e.action}</td>
                <td className="p-2">{e.actorEmail}</td>
                <td className="p-2 text-slate-500">{e.tenantId ?? '—'}</td>
                <td className="p-2"><code className="text-xs">{JSON.stringify(e.payload)}</code></td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {/* Modal — appears on Export click. Stays open until either an
          error surfaces (the operator must dismiss) or the success
          toast self-dismisses. */}
      {exportOpen && (
        <div
          className="fixed inset-0 bg-black/40 flex items-center justify-center z-50"
          role="dialog"
          aria-modal="true"
          aria-label="Audit-CSV-Export"
        >
          <div className="bg-white rounded shadow-lg p-6 w-96">
            <h3 className="text-lg font-semibold mb-3">CSV-Export</h3>
            {exportError ? (
              <>
                <p className="text-red-700 text-sm whitespace-pre-wrap">{exportError}</p>
                <button
                  type="button"
                  onClick={() => setExportOpen(false)}
                  className="mt-4 px-3 py-1 rounded bg-slate-200 text-sm"
                >
                  Schließen
                </button>
              </>
            ) : exportSuccess ? (
              <>
                <p className="text-green-700 text-sm">{exportSuccess}</p>
                <button
                  type="button"
                  onClick={() => setExportOpen(false)}
                  className="mt-4 px-3 py-1 rounded bg-neutral-900 text-white text-sm"
                >
                  Fertig
                </button>
              </>
            ) : (
              <>
                <p className="text-slate-700 text-sm mb-3">
                  Exportiere die gefilterten Audit-Einträge…
                  Bei föderierten Tenants kann das einige Sekunden dauern.
                </p>
                {/* Indeterminate progress bar — we can't know the row
                    count in advance without an extra round-trip, and
                    the streaming endpoint doesn't surface a total. */}
                <div className="w-full bg-slate-200 h-2 rounded overflow-hidden">
                  <div className="h-2 bg-primary-400 animate-pulse" style={{ width: '50%' }} />
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
